import { useState, useCallback, useEffect } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router'
import { AppShell, Sidebar, MobileSidebar, BottomTabBar, BetaBadge, type SidebarItem, type BottomTabItem } from '@provendoro/ui'
import { useAdminAuthContext } from '../auth/useAdminAuthContext.js'
import { BrandSwitcher } from './components/BrandSwitcher.js'
import { NotificationInbox } from './components/NotificationInbox.js'
import { GlobalSearch } from './components/GlobalSearch.js'

// ── Icons ──

function DashboardIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
    </svg>
  )
}

function OrdersIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
      <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
    </svg>
  )
}

function CateringIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path fillRule="evenodd" d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zM7 8H5v2h2V8zm2 0h2v2H9V8zm6 0h-2v2h2V8z" clipRule="evenodd" />
    </svg>
  )
}

function ProductsIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" />
      <path fillRule="evenodd" d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" clipRule="evenodd" />
    </svg>
  )
}

function InventoryIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM14 11a1 1 0 011 1v1h1a1 1 0 110 2h-1v1a1 1 0 11-2 0v-1h-1a1 1 0 110-2h1v-1a1 1 0 011-1z" />
    </svg>
  )
}

function WholesaleIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path d="M8 5a1 1 0 100 2h5.586l-1.293 1.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L13.586 5H8zM12 15a1 1 0 100-2H6.414l1.293-1.293a1 1 0 10-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L6.414 15H12z" />
    </svg>
  )
}

function RecipesIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
    </svg>
  )
}

function AnalyticsIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zm6-4a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zm6-3a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
    </svg>
  )
}

function SettingsIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
    </svg>
  )
}

function AlertsIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
    </svg>
  )
}

export function AdminLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAdminAuthContext()
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const [multiBrandEnabled, setMultiBrandEnabled] = useState(false)
  const [notifPanelOpen, setNotifPanelOpen] = useState(false)

  // Fetch tenant settings to check multi-brand mode
  useEffect(() => {
    fetch('/api/v1/admin/settings', { credentials: 'include' })
      .then((r) => r.json())
      .then((res: { data?: { multiBrandEnabled?: boolean } }) => {
        if (res.data?.multiBrandEnabled) setMultiBrandEnabled(true)
      })
      .catch(() => {})
  }, [])

  // Detect whether we're running inside the storefront (/vendor/*) or standalone dashboard
  const vendorBase = location.pathname.startsWith('/vendor') ? '/vendor' : ''

  const isAt = (path: string, exact = false) => {
    const full = `${vendorBase}${path}`
    if (exact) return location.pathname === full || (path === '/dashboard' && location.pathname === vendorBase) || (path === '/dashboard' && location.pathname === '/')
    return location.pathname.startsWith(full)
  }

  const go = (path: string) => {
    navigate(`${vendorBase}${path}`)
    setMobileSidebarOpen(false)
  }

  const navItems: SidebarItem[] = [
    {
      id: 'dashboard',
      label: 'Home',
      icon: <DashboardIcon />,
      active: isAt('/dashboard', true),
      onClick: () => go('/dashboard'),
    },
    {
      id: 'orders',
      label: 'Orders',
      icon: <OrdersIcon />,
      active: isAt('/orders') && !location.pathname.includes('pick-lists'),
      onClick: () => go('/orders'),
    },
    {
      id: 'catering',
      label: 'Catering',
      icon: <CateringIcon />,
      active: isAt('/catering'),
      onClick: () => go('/catering'),
    },
    {
      id: 'products',
      label: 'Products',
      icon: <ProductsIcon />,
      active: isAt('/products'),
      onClick: () => go('/products'),
    },
    {
      id: 'inventory',
      label: 'Inventory',
      icon: <InventoryIcon />,
      active: isAt('/inventory'),
      onClick: () => go('/inventory'),
    },
    {
      id: 'wholesale',
      label: 'Wholesale',
      icon: <WholesaleIcon />,
      active: isAt('/wholesale'),
      onClick: () => go('/wholesale/orders'),
    },
    {
      id: 'recipes',
      label: 'Recipes',
      icon: <RecipesIcon />,
      active: isAt('/recipes'),
      onClick: () => go('/recipes'),
    },
  ]

  const bottomNavItems: SidebarItem[] = [
    {
      id: 'analytics',
      label: 'Analytics',
      icon: <AnalyticsIcon />,
      active: isAt('/analytics'),
      onClick: () => go('/analytics'),
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: <SettingsIcon />,
      active: isAt('/settings'),
      onClick: () => go('/settings'),
    },
    {
      id: 'suppliers',
      label: 'Suppliers',
      icon: <OrdersIcon />,
      active: location.pathname.startsWith(`${vendorBase}/suppliers`),
      onClick: () => { navigate(`${vendorBase}/suppliers`); setMobileSidebarOpen(false) },
    },
    {
      id: 'purchasing',
      label: 'Purchase Orders',
      icon: <OrdersIcon />,
      active: location.pathname.startsWith(`${vendorBase}/purchasing`),
      onClick: () => { navigate(`${vendorBase}/purchasing`); setMobileSidebarOpen(false) },
    },
    {
      id: 'promotions',
      label: 'Promotions',
      icon: <AnalyticsIcon />,
      active: location.pathname.startsWith(`${vendorBase}/promotions`),
      onClick: () => { navigate(`${vendorBase}/promotions`); setMobileSidebarOpen(false) },
    },
  ]

  const handleLogout = useCallback(async () => {
    await logout()
    navigate(`${vendorBase}/login`)
  }, [logout, navigate, vendorBase])

  // All nav items for sidebar (settings removed - accessed via profile tile in header)
  const allNavItems: SidebarItem[] = [
    ...navItems,
    ...bottomNavItems.filter((i) => i.id !== 'settings'),
  ]

  const mobileTabItems: BottomTabItem[] = [
    { id: 'orders', label: 'Orders', icon: <OrdersIcon />, active: isAt('/orders') && !location.pathname.includes('pick-lists'), onClick: () => go('/orders') },
    { id: 'inventory', label: 'Inventory', icon: <InventoryIcon />, active: isAt('/inventory'), onClick: () => go('/inventory') },
    { id: 'catering', label: 'Catering', icon: <CateringIcon />, active: isAt('/catering'), onClick: () => go('/catering') },
    { id: 'alerts', label: 'Alerts', icon: <AlertsIcon />, active: location.pathname.includes('/inventory/low-stock') || location.pathname.includes('/alerts'), onClick: () => go('/inventory') },
  ]

  // Sidebar: just nav items + collapse toggle (no header, no footer)
  const sidebarContent = (
    <Sidebar
      items={allNavItems}
      collapsed={sidebarCollapsed}
      onCollapseChange={setSidebarCollapsed}
    />
  )

  // Mobile header
  const mobileHeader = (
    <div className="flex items-center justify-between px-4 py-3">
      <button
        type="button"
        onClick={() => setMobileSidebarOpen(true)}
        aria-label="Open menu"
        className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded"
        style={{ color: 'var(--color-text)' }}
      >
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>
      <span className="text-base font-bold" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-sans)' }}>Provendoro <BetaBadge size="sm" /></span>
      <NotificationInbox />
    </div>
  )

  // Desktop header: Provendoro left, search centered, notifications + profile right
  const desktopHeader = (
    <div className="flex items-center px-5 py-2.5">
      {/* Brand - left */}
      <div className="flex items-center gap-1.5 shrink-0 w-48">
        <span className="text-sm font-bold" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-sans)' }}>Provendoro</span>
        <BetaBadge size="sm" />
      </div>

      {/* Search - centered */}
      <div className="flex-1 flex justify-center">
        <div className="w-full max-w-md">
          <GlobalSearch />
        </div>
      </div>

      {/* Right side: brand switcher + notifications + profile tile */}
      <div className="flex items-center gap-2 shrink-0 w-48 justify-end">
        {multiBrandEnabled && (
          <div className="w-40">
            <BrandSwitcher />
          </div>
        )}

        {/* Notification bell */}
        <button
          type="button"
          onClick={() => setNotifPanelOpen(!notifPanelOpen)}
          aria-label="Notifications"
          className="relative flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-[var(--color-surface)]"
          style={{ color: notifPanelOpen ? 'var(--color-primary)' : 'var(--color-text-secondary)' }}
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
        </button>

        {/* Profile tile - navigates to settings */}
        <button
          type="button"
          onClick={() => go('/settings')}
          className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 transition-colors hover:bg-[var(--color-surface)]"
          style={{ border: '1px solid var(--color-border)' }}
        >
          <div className="flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold text-white shrink-0" style={{ backgroundColor: 'var(--color-primary)' }}>
            {user?.name?.charAt(0)?.toUpperCase() ?? 'U'}
          </div>
          <span className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>{user?.name}</span>
        </button>
      </div>
    </div>
  )

  // Notification right panel (pushes content)
  const notifPanel = notifPanelOpen ? (
    <aside
      className="hidden md:flex flex-col shrink-0 w-80 border-l overflow-y-auto"
      style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-background)' }}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--color-border)' }}>
        <h2 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>Notifications</h2>
        <button
          type="button"
          onClick={() => setNotifPanelOpen(false)}
          aria-label="Close notifications"
          className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-[var(--color-surface)] transition-colors"
          style={{ color: 'var(--color-text-muted)' }}
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="flex-1 p-4">
        <NotificationInbox inlineMode />
      </div>
    </aside>
  ) : null

  // Mobile sidebar
  const mobileSidebarHeader = (
    <div className="flex items-center gap-1.5">
      <span className="text-sm font-bold" style={{ color: 'var(--color-navy)', fontFamily: 'var(--font-sans)' }}>Provendoro</span>
      <BetaBadge size="sm" />
    </div>
  )
  const mobileSidebarFooter = (
    <div className="space-y-2">
      {user && <div className="truncate text-xs" style={{ color: 'var(--color-text-muted)' }}>{user.name}</div>}
      <button type="button" onClick={handleLogout} className="w-full rounded px-2 py-1 text-left text-sm hover:bg-[var(--color-surface)]" style={{ color: 'var(--color-text-secondary)' }}>Sign out</button>
    </div>
  )
  const mobileSidebarItems: SidebarItem[] = [
    ...allNavItems,
    {
      id: 'settings',
      label: 'Settings',
      icon: <SettingsIcon />,
      active: isAt('/settings'),
      onClick: () => go('/settings'),
    },
  ]

  return (
    <>
      <AppShell
        sidebar={sidebarContent}
        header={mobileHeader}
        desktopHeader={desktopHeader}
        sidebarCollapsed={sidebarCollapsed}
        rightPanel={notifPanel}
      >
        <div className="h-full overflow-auto pb-16 md:pb-0">
          <Outlet />
        </div>
      </AppShell>
      <MobileSidebar open={mobileSidebarOpen} onClose={() => setMobileSidebarOpen(false)}>
        <Sidebar items={mobileSidebarItems} header={mobileSidebarHeader} footer={mobileSidebarFooter} />
      </MobileSidebar>
      <BottomTabBar items={mobileTabItems} />
    </>
  )
}
